import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const CPOLanding = () => {
  useEffect(() => {
    // Track page view
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'pageview',
      page: '/cpo',
      audience: 'CPO'
    });
  }, []);

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>URGAA for CPOs - Complete Charging Station Management Platform | Go4Garage</title>
        <meta name="title" content="URGAA for CPOs - Complete Charging Station Management Platform" />
        <meta name="description" content="Manage your entire charging network with URGAA. DSAT site intelligence, automated SLA credits, real-time NOC operations. Trusted by 50+ CPOs across India." />
        <meta name="keywords" content="CPO platform India, charging station management, EV charging operations, charging network software, charge point operator software, EV infrastructure management" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://urgaa.in/cpo" />
        <meta property="og:title" content="URGAA for CPOs - Complete Charging Station Management Platform" />
        <meta property="og:description" content="Manage your entire charging network with URGAA. DSAT site intelligence, automated SLA credits, real-time NOC operations. Trusted by 50+ CPOs across India." />
        <meta property="og:image" content="https://urgaa.in/images/og-cpo.jpg" />
        <meta property="og:locale" content="en_IN" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://urgaa.in/cpo" />
        <meta name="twitter:title" content="URGAA for CPOs - Complete Charging Station Management Platform" />
        <meta name="twitter:description" content="Manage your entire charging network with URGAA. DSAT site intelligence, automated SLA credits, real-time NOC operations." />
        <meta name="twitter:image" content="https://urgaa.in/images/twitter-cpo.jpg" />
        <meta name="twitter:site" content="@Go4Garage" />
        
        {/* Additional Meta */}
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="author" content="Go4Garage Private Limited" />
        <meta name="geo.region" content="IN" />
        <link rel="canonical" href="https://urgaa.in/cpo" />
        
        {/* Schema Markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "URGAA for CPOs",
            "applicationCategory": "BusinessApplication",
            "offers": {
              "@type": "Offer",
              "price": "2000",
              "priceCurrency": "INR",
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "price": "2000",
                "priceCurrency": "INR",
                "unitText": "per station per month"
              }
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "ratingCount": "50"
            },
            "description": "Complete charging station management platform for CPOs",
            "operatingSystem": "Web-based, Cloud",
            "provider": {
              "@type": "Organization",
              "name": "Go4Garage Private Limited",
              "url": "https://urgaa.in"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              The Complete Operating System<br />for Charge Point Operators
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Manage your entire charging network with URGAA. DSAT site intelligence, automated SLA credits, and real-time NOC operations.
            </p>
            <div className="flex gap-4 justify-center">
              <Link to="/login" className="px-8 py-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold">
                Start Free 30-Day Trial
              </Link>
              <a href="#demo" className="px-8 py-4 border-2 border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 font-semibold">
                Watch Demo
              </a>
            </div>
            <p className="text-sm text-gray-500 mt-4">Trusted by 50+ CPOs across India | No credit card required</p>
          </div>
        </section>

        {/* Pain Points Section */}
        <section className="py-16 bg-red-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">Challenges CPOs Face Every Day</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="text-red-600 text-4xl mb-4">📉</div>
                <h3 className="text-xl font-bold mb-2">Site Selection Failures</h3>
                <p className="text-gray-600">Average 35% utilization. Millions invested in wrong locations.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="text-red-600 text-4xl mb-4">⚠️</div>
                <h3 className="text-xl font-bold mb-2">Downtime Losses</h3>
                <p className="text-gray-600">₹2-5 lakhs lost per station annually due to downtime.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="text-red-600 text-4xl mb-4">📊</div>
                <h3 className="text-xl font-bold mb-2">Manual Reconciliation</h3>
                <p className="text-gray-600">30 hours/month on SLA credit disputes and revenue reconciliation.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">URGAA Solution for CPOs</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="border-2 border-green-500 p-6 rounded-lg">
                <div className="text-green-600 text-4xl mb-4">🎯</div>
                <h3 className="text-xl font-bold mb-2">DSAT Intelligence</h3>
                <p className="text-gray-600 mb-4">AI-powered site selection achieving 80% utilization (vs industry 35%)</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ DemandIndex algorithm (0.4×Need + 0.25×Route + 0.2×SupplyGap + 0.15×Feasibility)</li>
                  <li>✓ AC vs DC recommendation</li>
                  <li>✓ Build vs Enroll strategy</li>
                </ul>
              </div>
              <div className="border-2 border-blue-500 p-6 rounded-lg">
                <div className="text-blue-600 text-4xl mb-4">💰</div>
                <h3 className="text-xl font-bold mb-2">Automated SLA Credits</h3>
                <p className="text-gray-600 mb-4">Zero disputes. Automated uptime tracking and credit calculation.</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ Uptime SLA (98% target)</li>
                  <li>✓ MTTR credits (>8h threshold)</li>
                  <li>✓ MTTA credits (>15min threshold)</li>
                </ul>
              </div>
              <div className="border-2 border-purple-500 p-6 rounded-lg">
                <div className="text-purple-600 text-4xl mb-4">🚨</div>
                <h3 className="text-xl font-bold mb-2">Real-time NOC</h3>
                <p className="text-gray-600 mb-4">60% MTTR reduction with intelligent ticketing and field tech routing.</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ Live station monitoring</li>
                  <li>✓ Automated fault detection</li>
                  <li>✓ GPS-based field tech dispatch</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-4">Simple, Transparent Pricing</h2>
            <div className="bg-white p-8 rounded-lg shadow-lg inline-block">
              <div className="text-5xl font-bold text-purple-600 mb-2">₹2,000</div>
              <div className="text-gray-600 mb-6">per station per month</div>
              <ul className="text-left space-y-3 mb-8">
                <li>✓ Unlimited users</li>
                <li>✓ All modules included (DSAT, NOC, Revenue, Governance)</li>
                <li>✓ 24/7 support</li>
                <li>✓ Free onboarding and training</li>
                <li>✓ OCPP 1.6 & 2.0 integration</li>
              </ul>
              <Link to="/login" className="w-full block py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold">
                Start Free Trial
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-purple-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold mb-4">Ready to Transform Your Charging Network?</h2>
            <p className="text-xl mb-8">Join 50+ CPOs managing 1,000+ stations with URGAA</p>
            <Link to="/login" className="px-8 py-4 bg-white text-purple-600 rounded-lg hover:bg-gray-100 font-semibold inline-block">
              Start Free 30-Day Trial →
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default CPOLanding;
